/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  memo,
  useCallback,
  useState,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { View, Pressable, Animated, Easing } from 'react-native';
import {
  getColor,
  getShape,
  pd,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import { useDeviceInfo } from '@airasia-phoenix/react-native/utils';
import { StyleSheet } from 'react-native-unistyles';
import {
  DividerView,
  DividerType,
} from '@airasia-phoenix/react-native/components';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import InputStepperView from '@airasia-phoenix/react-native/components/InputStepperView';

import {
  RoomConfig,
  HotelGuestSelectionProps,
  RoomHeaderProps,
  AgePickerButtonProps,
} from '../../types';
import { getChildAgeLabel, getUpdatedChildrenArray } from './utils';
import { DEFAULT_CONFIG_VALUES } from './constants';
import ChildAgePickerSheet from './ChildAgePickerSheet';
import type { BottomSheetRefs } from '@airasia-phoenix/react-native/components/BottomSheetView';
import { useTranslation } from 'react-i18next';

const GuestSelectionView = ({
  rooms,
  onUpdateRooms,
  config,
  isParentModal = false,
}: HotelGuestSelectionProps) => {
  const { t } = useTranslation('hotels');

  const { isWeb, isMobileBrowser } = useDeviceInfo();
  const isDesktopWeb = isWeb && !isMobileBrowser;

  const [isWebChildAgePickerVisible, setIsWebChildAgePickerVisible] =
    useState(false);
  const childAgeSheetRef = useRef<BottomSheetRefs>(null);
  const [activeChild, setActiveChild] = useState<{
    rIdx: number;
    cIdx: number;
  } | null>(null);

  const [webChildAgeAnchorLayout, setWebChildAgeAnchorLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const updateRoomData = useCallback(
    (index: number, updatedRoom: RoomConfig) => {
      const newRooms = [...rooms];
      newRooms[index] = updatedRoom;
      onUpdateRooms(newRooms);
    },
    [rooms, onUpdateRooms],
  );

  const isAddingRoom = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<View>(null);
  const roomAnimValues = useRef<Record<string, Animated.Value>>({});
  const removingRoomIds = useRef<Set<string>>(new Set());

  const getRoomAnimValue = useCallback((roomId: string) => {
    if (!roomAnimValues.current[roomId]) {
      roomAnimValues.current[roomId] = new Animated.Value(1);
    }
    return roomAnimValues.current[roomId];
  }, []);

  // Calculate total guests across all rooms
  const totalGuests = useMemo(() => {
    return rooms.reduce((total, room) => {
      return total + room.adults + room.children.length + (room.infants || 0);
    }, 0);
  }, [rooms]);

  // Check if max guest count is reached (unlimited if MAX_TOTAL_GUESTS is undefined)
  const isMaxGuestsReached = config.MAX_TOTAL_GUESTS
    ? totalGuests >= config.MAX_TOTAL_GUESTS
    : false;

  const handleAddRoom = useCallback(() => {
    if (isAddingRoom.current) return;
    if (rooms.length < config.MAX_ROOMS) {
      isAddingRoom.current = true;
      const newRoomId = `room-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Calculate remaining guest capacity (unlimited if MAX_TOTAL_GUESTS is undefined)
      const remainingCapacity = config.MAX_TOTAL_GUESTS
        ? config.MAX_TOTAL_GUESTS - totalGuests
        : 2;
      const defaultAdults = Math.min(2, Math.max(1, remainingCapacity));

      onUpdateRooms(
        [...rooms, { id: newRoomId, adults: defaultAdults, children: [] }],
        true, // isAdding flag to trigger auto-scroll
      );
      timeoutRef.current = setTimeout(() => {
        isAddingRoom.current = false;
      }, 300);
    }
  }, [rooms, onUpdateRooms, config, totalGuests]);

  const onInfantCountChange = useCallback(
    (rIdx: number, count: number) => {
      const room = rooms[rIdx];

      // Business rule: Each infant must be accompanied by an adult
      // Infants cannot exceed the number of adults
      if (count > room.adults) {
        return; // Don't allow more infants than adults
      }

      const proposedPax = room.adults + room.children.length + count;

      // Ensure total pax doesn't exceed config limit and respects config limits
      if (
        proposedPax <= config.MAX_ADULTS_PER_ROOM &&
        (!config.MAX_INFANTS_PER_ROOM || count <= config.MAX_INFANTS_PER_ROOM)
      ) {
        updateRoomData(rIdx, { ...room, infants: count });
      }
    },
    [rooms, updateRoomData, config],
  );

  const handleRemoveRoom = useCallback(
    (index: number) => {
      const targetRoom = rooms[index];

      if (!targetRoom || removingRoomIds.current.has(targetRoom.id)) {
        return;
      }

      removingRoomIds.current.add(targetRoom.id);
      const roomAnimValue = getRoomAnimValue(targetRoom.id);

      Animated.timing(roomAnimValue, {
        toValue: 0,
        duration: 180,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (!finished) {
          removingRoomIds.current.delete(targetRoom.id);
          roomAnimValue.setValue(1);
          return;
        }

        const newRooms = rooms.filter((room) => room.id !== targetRoom.id);
        onUpdateRooms(newRooms);

        removingRoomIds.current.delete(targetRoom.id);
        delete roomAnimValues.current[targetRoom.id];
      });
    },
    [rooms, onUpdateRooms, getRoomAnimValue],
  );

  const getChildSecondaryText = useCallback(
    (childrenCount: number) =>
      t(
        childrenCount > 0
          ? 'searchHotelPage.guestSelection.childSubTextWithChildren'
          : 'searchHotelPage.guestSelection.childSubTextNoChildren',
        {
          minAge: config.MIN_CHILD_AGE ?? DEFAULT_CONFIG_VALUES.MIN_CHILD_AGE,
          maxAge: config.MAX_CHILD_AGE,
        },
      ),
    [t, config.MIN_CHILD_AGE, config.MAX_CHILD_AGE],
  );

  const onChildCountChange = useCallback(
    (rIdx: number, count: number) => {
      const room = rooms[rIdx];
      const updatedChildren = getUpdatedChildrenArray(
        room.children,
        count,
        config.DEFAULT_CHILD_AGE_SELECTION ??
          DEFAULT_CONFIG_VALUES.DEFAULT_CHILD_AGE_SELECTION,
      );
      updateRoomData(rIdx, { ...room, children: updatedChildren });
    },
    [rooms, updateRoomData, config.DEFAULT_CHILD_AGE_SELECTION],
  );

  const onAgeSelected = useCallback(
    (age: number) => {
      if (!activeChild) return;

      const { rIdx, cIdx } = activeChild;
      const room = rooms[rIdx];

      // Create new children array with updated age
      const updatedChildren = room.children.map((child, index) =>
        index === cIdx ? { ...child, age } : child,
      );

      updateRoomData(rIdx, { ...room, children: updatedChildren });
    },
    [activeChild, rooms, updateRoomData],
  );

  const onAdultCountChange = useCallback(
    (rIdx: number, adultCount: string | number) => {
      const room = rooms[rIdx];

      if (!room) {
        return;
      }

      const parsedAdultCount = Number(adultCount);
      const maxAdultCount = Math.max(
        config.MIN_ADULTS_PER_ROOM,
        isMaxGuestsReached ? room.adults : config.MAX_ADULTS_PER_ROOM,
      );
      const newAdultCount =
        Number.isFinite(parsedAdultCount) && Number.isInteger(parsedAdultCount)
          ? Math.min(
              Math.max(parsedAdultCount, config.MIN_ADULTS_PER_ROOM),
              maxAdultCount,
            )
          : config.MIN_ADULTS_PER_ROOM;

      // Business rule: Infants cannot exceed adults
      // If reducing adults, adjust infant count accordingly
      const currentInfantCount = room.infants || 0;
      const newInfantCount = Math.min(currentInfantCount, newAdultCount);

      updateRoomData(rIdx, {
        ...room,
        adults: newAdultCount,
        infants: newInfantCount,
      });
    },
    [
      config.MAX_ADULTS_PER_ROOM,
      config.MIN_ADULTS_PER_ROOM,
      isMaxGuestsReached,
      rooms,
      updateRoomData,
    ],
  );

  const getChildAgeTitle = useCallback(
    (count: number) =>
      t('searchHotelPage.guestSelection.childAgeTitle', { count }),
    [t],
  );

  const onChildAgePickerPress = useCallback(
    (rIdx: number, cIdx: number) => {
      setActiveChild({ rIdx, cIdx });

      if (isParentModal) {
        const el = document.querySelector(
          `[data-testid="child-age-trigger-${rIdx}-${cIdx}"]`,
        );
        const el2 = document.getElementById(`child-age-anchor-${rIdx}-${cIdx}`);
        const rect = el2
          ? el2.getBoundingClientRect()
          : el?.getBoundingClientRect?.();

        if (rect) {
          setWebChildAgeAnchorLayout({
            x: rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
          });
        }
        setIsWebChildAgePickerVisible(true);
      } else {
        setTimeout(() => {
          if (childAgeSheetRef.current) {
            childAgeSheetRef.current.toggleBottomSheetView();
          }
        }, 0);
      }
    },
    [isParentModal],
  );

  useEffect(() => {
    if (
      !isWebChildAgePickerVisible ||
      !isParentModal ||
      !activeChild ||
      typeof window === 'undefined'
    ) {
      return;
    }

    const updateAnchorLayout = () => {
      const { rIdx, cIdx } = activeChild;
      const el2 = document.getElementById(`child-age-anchor-${rIdx}-${cIdx}`);
      const el = document.querySelector(
        `[data-testid="child-age-trigger-${rIdx}-${cIdx}"]`,
      );
      const rect = el2
        ? el2.getBoundingClientRect()
        : el?.getBoundingClientRect?.();

      if (rect) {
        setWebChildAgeAnchorLayout({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    window.addEventListener('scroll', updateAnchorLayout, true);
    window.addEventListener('resize', updateAnchorLayout);

    return () => {
      window.removeEventListener('scroll', updateAnchorLayout, true);
      window.removeEventListener('resize', updateAnchorLayout);
    };
  }, [isWebChildAgePickerVisible, isParentModal, activeChild]);

  const roomHeaderCallbacks = useMemo(() => {
    const callbacks: { [key: number]: () => void } = {};
    rooms.forEach((_, index) => {
      callbacks[index] = () => handleRemoveRoom(index);
    });
    return callbacks;
  }, [rooms, handleRemoveRoom]);

  const selectedChildAge = activeChild
    ? rooms[activeChild.rIdx]?.children[activeChild.cIdx]?.age
    : (config.DEFAULT_CHILD_AGE_SELECTION ??
      DEFAULT_CONFIG_VALUES.DEFAULT_CHILD_AGE_SELECTION);

  const childAgePickerProps = useMemo(
    () => ({
      title: t('searchHotelPage.guestSelection.childAgeTitle', {
        count: activeChild ? Number(activeChild.cIdx + 1) : 1,
      }),
      selectedAge: selectedChildAge,
      onSelectAge: onAgeSelected,
      onClose: () => setActiveChild(null),
    }),
    [t, activeChild, selectedChildAge, onAgeSelected],
  );

  // Platform-specific props
  const webProps = useMemo(
    () => ({
      isVisible: isWebChildAgePickerVisible,
      onRequestClose: () => {
        setIsWebChildAgePickerVisible(false);
        setActiveChild(null);
      },
      anchorLayout: webChildAgeAnchorLayout,
    }),
    [isWebChildAgePickerVisible, webChildAgeAnchorLayout],
  );

  return (
    <View
      ref={containerRef}
      testID="guest-selection-container"
      pointerEvents="box-none"
    >
      {rooms.map((room, rIdx) => {
        const childSecondaryText = getChildSecondaryText(room.children.length);
        const roomAnimValue = getRoomAnimValue(room.id);
        return (
          <Animated.View
            key={room.id}
            style={[
              styles.roomCard,
              {
                opacity: roomAnimValue,
              },
            ]}
            testID={`room-block-${rIdx}`}
            pointerEvents="box-none"
          >
            {rooms.length > 1 && (
              <RoomHeader
                index={rIdx}
                onRemove={roomHeaderCallbacks[rIdx]}
                isFirst={rIdx === 0}
              />
            )}

            <View style={styles.passengerRow}>
              <View style={styles.passengerText}>
                <TextView
                  text={t('searchHotelPage.guestSelection.adult')}
                  style={getTextStyle(
                    pd.sys.typescale.body_text_medium,
                    pd.sys.colour.content_primary,
                  )}
                />
                <TextView
                  text={t('searchHotelPage.guestSelection.adultSubText', {
                    minAge: config.MAX_CHILD_AGE + 1,
                  })}
                  style={getTextStyle(
                    pd.sys.typescale.caption_text,
                    pd.sys.colour.content_secondary,
                  )}
                />
              </View>
              <View style={styles.stepperContainer}>
                <InputStepperView
                  initialValue={room.adults}
                  minValue={config.MIN_ADULTS_PER_ROOM}
                  maxValue={
                    isMaxGuestsReached
                      ? room.adults
                      : config.MAX_ADULTS_PER_ROOM
                  }
                  onChange={(val) => onAdultCountChange(rIdx, val)}
                />
              </View>
            </View>

            <View style={styles.passengerRow}>
              <View style={styles.passengerText}>
                <TextView
                  text={t('searchHotelPage.guestSelection.child')}
                  style={getTextStyle(
                    pd.sys.typescale.body_text_medium,
                    pd.sys.colour.content_primary,
                  )}
                />
                <TextView
                  text={childSecondaryText}
                  style={getTextStyle(
                    pd.sys.typescale.caption_text,
                    pd.sys.colour.content_secondary,
                  )}
                />
              </View>
              <View style={styles.stepperContainer}>
                <InputStepperView
                  initialValue={room.children.length}
                  minValue={config.MIN_CHILDREN_PER_ROOM}
                  maxValue={
                    isMaxGuestsReached
                      ? room.children.length
                      : config.MAX_CHILDREN_PER_ROOM
                  }
                  onChange={(val) => onChildCountChange(rIdx, Number(val))}
                />
              </View>
            </View>

            {room.children.length > 0 && (
              <View style={styles.ageContainer}>
                {room.children.map((child, cIdx) => (
                  <AgePickerButton
                    key={child.id}
                    label={getChildAgeTitle(cIdx + 1)}
                    ageText={getChildAgeLabel(child.age, t)}
                    isFullWidth={room.children.length === 1}
                    testID={`child-age-trigger-${rIdx}-${cIdx}`}
                    onPress={() => onChildAgePickerPress(rIdx, cIdx)}
                    isDesktopWeb={isDesktopWeb}
                    isActive={
                      activeChild?.rIdx === rIdx && activeChild?.cIdx === cIdx
                    }
                  />
                ))}
              </View>
            )}

            {config.MAX_INFANTS_PER_ROOM && config.MAX_INFANTS_PER_ROOM > 0 && (
              <View style={styles.passengerRow}>
                <View style={styles.passengerText}>
                  <TextView
                    text={t('searchHotelPage.guestSelection.infant')}
                    style={getTextStyle(
                      pd.sys.typescale.body_text_medium,
                      pd.sys.colour.content_primary,
                    )}
                  />
                  <TextView
                    text={t('searchHotelPage.guestSelection.infantSubText')}
                    style={getTextStyle(
                      pd.sys.typescale.caption_text,
                      pd.sys.colour.content_secondary,
                    )}
                  />
                </View>
                <View style={styles.stepperContainer}>
                  <InputStepperView
                    initialValue={room.infants || 0}
                    minValue={config.MIN_INFANTS_PER_ROOM}
                    maxValue={
                      isMaxGuestsReached
                        ? room.infants || 0
                        : Math.min(
                            config.MAX_INFANTS_PER_ROOM || 0,
                            room.adults,
                          )
                    }
                    onChange={(val) => onInfantCountChange(rIdx, Number(val))}
                  />
                </View>
              </View>
            )}
          </Animated.View>
        );
      })}

      {rooms.length < config.MAX_ROOMS && (
        <Pressable
          style={[
            styles.addRoomButton,
            isMaxGuestsReached && styles.addRoomButtonDisabled,
          ]}
          onPress={handleAddRoom}
          disabled={isMaxGuestsReached}
          testID="add-room-btn"
        >
          <IconView
            icon={pd.icon.main.content.add}
            tint={getColor(
              isMaxGuestsReached
                ? pd.sys.colour.content_secondary
                : pd.sys.colour.interactive_link,
            )}
            width={24}
            height={24}
          />
          <TextView
            text={t('searchHotelPage.guestSelection.addRoom')}
            style={getTextStyle(
              pd.sys.typescale.body_text_medium,
              isMaxGuestsReached
                ? pd.sys.colour.content_secondary
                : pd.sys.colour.interactive_link,
            )}
          />
        </Pressable>
      )}
      {activeChild && (
        <ChildAgePickerSheet
          config={config}
          bottomSheetRef={childAgeSheetRef}
          isParentModal={isParentModal}
          {...childAgePickerProps}
          {...(isParentModal ? webProps : {})}
        />
      )}
    </View>
  );
};

const RoomHeader = memo(({ index, onRemove, isFirst }: RoomHeaderProps) => {
  const { t } = useTranslation('hotels');

  return (
    <>
      <View style={styles.roomHeader}>
        <TextView
          text={`${t('searchHotelPage.guestSelection.room')} ${index + 1}`}
          style={getTextStyle(
            pd.sys.typescale.body_text_medium,
            pd.sys.colour.content_primary,
          )}
        />
        {!isFirst && (
          <Pressable onPress={onRemove} testID={`remove-room-${index}`}>
            <TextView
              text={t('searchHotelPage.guestSelection.removeRoom')}
              style={getTextStyle(
                pd.sys.typescale.body_text_medium,
                pd.sys.colour.interactive_link,
              )}
            />
          </Pressable>
        )}
      </View>
      <View style={styles.headerDivider}>
        <DividerView
          type={DividerType.Solid}
          color={getColor(pd.sys.colour.outline_low)}
          thickness={1}
          width="100%"
        />
      </View>
    </>
  );
});

RoomHeader.displayName = 'RoomHeader';

const AgePickerButton = memo(
  ({
    label,
    ageText,
    isFullWidth,
    isDesktopWeb,
    onPress,
    testID,
    isActive = false,
  }: AgePickerButtonProps) => {
    return (
      <View
        nativeID={
          isDesktopWeb && testID
            ? testID.replace('child-age-trigger', 'child-age-anchor')
            : undefined
        }
        style={[
          styles.agePicker,
          isFullWidth ? styles.fullWidth : styles.halfWidth,
        ]}
      >
        <Pressable style={styles.ageDropDown} onPress={onPress} testID={testID}>
          <View>
            <TextView
              text={label}
              style={getTextStyle(
                pd.sys.typescale.caption_text,
                pd.sys.colour.content_secondary,
              )}
            />
            <TextView
              text={ageText}
              style={getTextStyle(
                pd.sys.typescale.body_text,
                pd.sys.colour.content_primary,
              )}
            />
          </View>
          <View style={isActive ? styles.iconRotated : undefined}>
            <IconView
              icon={pd.icon.main.navigation.arrow_drop_down}
              tint={getColor(pd.sys.colour.content_primary)}
              width={24}
              height={24}
            />
          </View>
        </Pressable>
      </View>
    );
  },
);

AgePickerButton.displayName = 'AgePickerButton';

const styles = StyleSheet.create({
  roomCard: {
    paddingHorizontal: pd.ref.space.spacing20.px,
    marginTop: 12,
  },
  passengerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    marginBottom: pd.ref.space.spacing30.px,
  },
  passengerText: {
    flexDirection: 'column',
    flexShrink: 1,
    flexGrow: 1,
    paddingRight: pd.ref.space.spacing10.px,
  },
  stepperContainer: {
    paddingRight: pd.ref.space.spacing10.px,
  },
  roomHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerDivider: { marginTop: 12, marginBottom: 16 },
  stepperSpacing: { marginBottom: pd.ref.space.spacing30.px },
  ageContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  agePicker: { marginBottom: 12 },
  fullWidth: { width: '100%' },
  halfWidth: { width: '48.5%' },
  ageDropDown: {
    paddingHorizontal: pd.ref.space.spacing40.px,
    borderWidth: 1,
    borderColor: getColor(pd.sys.colour.outline_medium),
    ...getShape(pd.sys.shape.corner.medium),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 48,
  },
  addRoomButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: pd.ref.space.spacing20.px,
    marginTop: 8,
    marginBottom: pd.ref.space.spacing20.px,
    gap: 8,
    alignSelf: 'flex-start',
    width: 'auto',
  },
  addRoomButtonDisabled: {
    opacity: 0.5,
  },
  iconRotated: {
    transform: [{ rotate: '180deg' }],
  },
});

export default GuestSelectionView;
